/*
 * HOME4HEROES personalisation validator for Ella 7.2.0.
 *
 * WHY THIS EXISTS
 * ---------------
 * Both Minimog and Ella put `novalidate` on the product form, so the `required`
 * attribute alone enforces nothing. Worse, Ella's own collector in
 * assets/product-form.js does this:
 *
 *     const properties = document.querySelectorAll('.product-form__input [name^="properties"]')
 *     properties.forEach(property => {
 *       if (property.value == null || property.value === '') return;   // <-- SKIPS EMPTIES
 *       formData.append(property.name, property.value);
 *     })
 *
 * so a blank required field is silently dropped and the item is added to the
 * cart with NO personalisation. On a made-to-order product that is an
 * unfulfillable order that has already taken money.
 *
 * ELLA'S REAL SUBMIT PATHS - read from Ella's own code, not inferred from
 * Minimog's shape (OM44 section 2, step 4):
 *
 *   1. AJAX add-to-cart: `<product-form-component>` binds
 *      `form.addEventListener('submit', this.onSubmitHandler)` and calls
 *      `evt.preventDefault()`.
 *   2. Accelerated checkout: `{{ form | payment_button }}` sits INSIDE the same
 *      `{% form 'product' %}` in snippets/buy-buttons.liquid.
 *   3. TWO different add-to-cart renderers exist:
 *        snippets/buy-buttons.liquid  -> `.product-form__submit`
 *        blocks/add-to-cart.liquid    -> `.add-to-cart-button`
 *      Ella's own product-form.js only looks for `.add-to-cart-button`.
 *      Both are handled here.
 *
 * Ella's only existing gate, `initAgreeCondition()`, is NOT copied: it merely
 * toggles a presentational `.disabled` class on the payment button and adds a
 * `submit` listener. Accelerated wallets generally do not dispatch a form
 * `submit` event, so a class alone does not stop them. This uses capture-phase
 * pointer/click interception on the wallet container instead, which fires
 * before the wallet's own handler.
 *
 * A clean static check is NECESSARY, NOT SUFFICIENT. Per-template browser tests
 * are still required: blank submit blocked on add-to-cart AND on every
 * accelerated button, cart-line keys compared character for character, the
 * select constrained to its 24 values, the caps still stopping at 30, and both
 * image uploads reaching the order.
 */
(function () {
  'use strict';

  var FIELD = '[data-h4h-field]';
  var CONTROL = '[data-h4h-control]';
  var ERROR = '[data-h4h-error]';

  function fieldsIn(scope) {
    return Array.prototype.slice.call(scope.querySelectorAll(FIELD));
  }

  /*
   * MEASURED, and it invalidated the obvious implementation: on Ella the
   * personalisation fields render OUTSIDE `product-form-component` and outside
   * the product <form> - `control.closest('form')` was null. Scoping to the
   * form container therefore made every button guard blind
   * (`h4hValidatePersonalisation()` returned [] and a blank add-to-cart click
   * was NOT blocked), even though the submit guard still fired via its
   * document fallback.
   *
   * So walk OUTWARDS from the clicked element and take the first ancestor that
   * actually contains personalisation fields; fall back to document. That keeps
   * the guard correct whether the theme nests the fields inside the form or
   * beside it.
   */
  function scopeOf(el) {
    var candidates = [
      'product-info',
      '[id^="shopify-section"]',
      '.product__info-wrapper',
      'main',
    ];
    for (var i = 0; i < candidates.length; i++) {
      var n = el.closest(candidates[i]);
      if (n && fieldsIn(n).length) return n;
    }
    // last resort: the whole document, which definitely contains them
    return fieldsIn(document).length ? document : el.ownerDocument || document;
  }

  function valueOf(field) {
    var c = field.querySelector(CONTROL);
    if (!c) return '';
    if (c.type === 'file') return c.files && c.files.length ? 'file' : '';
    return (c.value || '').trim();
  }

  function setError(field, message) {
    var slot = field.querySelector(ERROR);
    var control = field.querySelector(CONTROL);
    if (slot) {
      slot.textContent = message || '';
      slot.hidden = !message;
    }
    if (control) {
      if (message) {
        control.setAttribute('aria-invalid', 'true');
      } else {
        control.removeAttribute('aria-invalid');
      }
    }
    field.classList.toggle('h4h-custom-field--invalid', !!message);
  }

  /**
   * Returns the list of invalid fields. Empty means OK.
   */
  function validate(scope, focus) {
    var bad = [];
    fieldsIn(scope).forEach(function (field) {
      var required = field.getAttribute('data-h4h-required') === 'true';
      var label = field.getAttribute('data-h4h-label') || 'This field';
      var control = field.querySelector(CONTROL);
      var value = valueOf(field);

      setError(field, '');
      if (required && !value) {
        setError(field, label + ' is required.');
        bad.push(field);
        return;
      }
      // the cap is advisory, but do not let a too-long value through silently
      if (control && control.maxLength && control.maxLength > 0 &&
          value.length > control.maxLength) {
        setError(field, label + ' must be ' + control.maxLength + ' characters or fewer.');
        bad.push(field);
      }
    });

    if (bad.length && focus) {
      var first = bad[0].querySelector(CONTROL);
      if (first && typeof first.focus === 'function') {
        try { first.scrollIntoView({ block: 'center' }); } catch (e) {}
        first.focus();
      }
    }
    return bad;
  }

  function block(e) {
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
  }

  /* ---------------------------------------------------------------- path 1
     AJAX add-to-cart. Capture phase so this runs BEFORE
     product-form-component's own submit handler.                            */
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var scope = scopeOf(form);
      if (!fieldsIn(scope).length) return;
      if (validate(scope, true).length) block(e);
    },
    true
  );

  /* ---------------------------------------------------------------- path 2
     Both add-to-cart button renderers. Guards the case where a click handler
     posts directly instead of submitting the form.                          */
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest(
        '.product-form__submit, .add-to-cart-button, button[name="add"]'
      );
      if (!btn) return;
      var scope = scopeOf(btn);
      if (!fieldsIn(scope).length) return;
      if (validate(scope, true).length) block(e);
    },
    true
  );

  /* ---------------------------------------------------------------- path 3
     ACCELERATED CHECKOUT - the path that can take money for an unfulfillable
     order. Shop Pay / PayPal / Google Pay render inside
     `.shopify-payment-button` and generally do NOT dispatch a form `submit`,
     so intercept the interaction itself in the capture phase.               */
  ['pointerdown', 'mousedown', 'click', 'keydown'].forEach(function (type) {
    document.addEventListener(
      type,
      function (e) {
        if (type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
        var wallet = e.target.closest(
          '.shopify-payment-button, .shopify-payment-button__button, ' +
          '[data-shopify="payment-button"], shopify-accelerated-checkout, ' +
          'shopify-buyer-consent'
        );
        if (!wallet) return;
        var scope = scopeOf(wallet);
        if (!fieldsIn(scope).length) return;
        if (validate(scope, true).length) block(e);
      },
      true
    );
  });

  /* Keep the wallet visually and semantically disabled until the form is
     complete, so the block is announced rather than felt as a dead click. */
  function reflect() {
    var fields = fieldsIn(document);
    if (!fields.length) return;
    var incomplete = fields.some(function (f) {
      return f.getAttribute('data-h4h-required') === 'true' && !valueOf(f);
    });
    document.querySelectorAll('.shopify-payment-button').forEach(function (w) {
      w.classList.toggle('h4h-wallet-blocked', incomplete);
      w.setAttribute('aria-disabled', incomplete ? 'true' : 'false');
    });
  }

  document.addEventListener('input', function (e) {
    if (e.target.closest(FIELD)) {
      setError(e.target.closest(FIELD), '');
      reflect();
    }
  });
  document.addEventListener('change', function (e) {
    if (e.target.closest(FIELD)) {
      setError(e.target.closest(FIELD), '');
      reflect();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reflect);
  } else {
    reflect();
  }
  document.addEventListener('shopify:section:load', reflect);
  document.addEventListener('cart:updated', reflect);

  // expose for QA
  window.h4hValidatePersonalisation = function () {
    var f = fieldsIn(document);
    if (!f.length) return { fields: 0, invalid: 0, note: 'no personalisation on this page' };
    var bad = validate(document, false);
    return {
      fields: f.length,
      invalid: bad.length,
      invalidLabels: bad.map(function (x) { return x.getAttribute('data-h4h-label'); }),
    };
  };
})();
